"use strict";
/**
 * Local **working stream** for note editing — independent of pilot_notes DB.
 *
 * VS Code–style idea:
 * - The DB row is only updated when the user explicitly saves.
 * - While brainstorming, we keep a durable local working copy (and optional
 *   checkpoints) that can restore the UI at any time.
 *
 * This stream is intentionally simple and aggressive:
 * - Always overwrite the latest working body when asked (no “not dirty” skip).
 * - Optional checkpoints for multi-generation recovery.
 * - Can be dropped after successful server save or user discard.
 *
 * Keys: audroam.editWorking:{personId}:{mode}:{rowId}
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.workingDiffersFromBody = exports.clearEditWorkingStream = exports.writeEditWorkingStream = exports.readEditWorkingStream = exports.editWorkingStorageKey = exports.EDIT_WORKING_CHECKPOINT_MIN_MS = exports.EDIT_WORKING_MAX_CHECKPOINTS = exports.EDIT_WORKING_VERSION = exports.EDIT_WORKING_PREFIX = void 0;
exports.EDIT_WORKING_PREFIX = 'audroam.editWorking';
exports.EDIT_WORKING_VERSION = 1;
/** Keep a short ring of checkpoints for recovery after bad intermediate states. */
exports.EDIT_WORKING_MAX_CHECKPOINTS = 12;
/** Min ms between automatic checkpoints (every write still updates `body`). */
exports.EDIT_WORKING_CHECKPOINT_MIN_MS = 8000;
function editWorkingStorageKey(personId, mode, rowId) {
    var pid = personId != null && personId !== '' ? String(personId) : 'anon';
    var rid = rowId != null && rowId !== '' ? String(rowId) : 'unknown';
    return "".concat(exports.EDIT_WORKING_PREFIX, ":").concat(pid, ":").concat(mode, ":").concat(rid);
}
exports.editWorkingStorageKey = editWorkingStorageKey;
function readEditWorkingStream(storage, personId, mode, rowId) {
    if (!storage)
        return null;
    try {
        var raw = storage.getItem(editWorkingStorageKey(personId, mode, rowId));
        if (!raw)
            return null;
        var parsed = JSON.parse(raw);
        if (!parsed || parsed.v !== exports.EDIT_WORKING_VERSION || typeof parsed.body !== 'string') {
            return null;
        }
        return parsed;
    }
    catch (_a) {
        return null;
    }
}
exports.readEditWorkingStream = readEditWorkingStream;
/**
 * Write/overwrite the working stream. Always stores latest `body`.
 * Checkpoints append when body changed and enough time passed (or forceCheckpoint).
 */
function writeEditWorkingStream(storage, input) {
    if (!storage || input.rowId === undefined || input.rowId === null) {
        return null;
    }
    var body = input.body == null ? '' : String(input.body);
    var key = editWorkingStorageKey(input.personId, input.mode, input.rowId);
    var now = new Date().toISOString();
    var source = input.source || 'unknown';
    var prev = null;
    try {
        var raw = storage.getItem(key);
        if (raw)
            prev = JSON.parse(raw);
    }
    catch (_a) {
        prev = null;
    }
    var checkpoints = Array.isArray(prev === null || prev === void 0 ? void 0 : prev.checkpoints) ? __spreadArray([], prev.checkpoints, true) : [];
    var bodyChanged = !prev || prev.body !== body;
    var lastCp = checkpoints[0];
    var lastCpAt = lastCp ? Date.parse(lastCp.at) : 0;
    var elapsed = Date.now() - (isNaN(lastCpAt) ? 0 : lastCpAt);
    var shouldCheckpoint = bodyChanged &&
        (input.forceCheckpoint ||
            input.source === 'emergency' ||
            input.source === 'map' ||
            elapsed >= exports.EDIT_WORKING_CHECKPOINT_MIN_MS ||
            !lastCp);
    if (shouldCheckpoint && bodyChanged) {
        checkpoints.unshift({ body: body, at: now, source: source });
        while (checkpoints.length > exports.EDIT_WORKING_MAX_CHECKPOINTS) {
            checkpoints.pop();
        }
    }
    var snap = {
        v: exports.EDIT_WORKING_VERSION,
        personId: input.personId != null ? String(input.personId) : 'anon',
        mode: input.mode,
        rowId: String(input.rowId),
        body: body,
        fields: input.fields != null ? input.fields : prev === null || prev === void 0 ? void 0 : prev.fields,
        ui: input.ui != null ? input.ui : prev === null || prev === void 0 ? void 0 : prev.ui,
        updatedAt: now,
        source: source,
        checkpoints: checkpoints,
    };
    try {
        storage.setItem(key, JSON.stringify(snap));
        return snap;
    }
    catch (_b) {
        // Quota or private mode — try body-only shrink
        try {
            var slim = __assign(__assign({}, snap), { fields: undefined, ui: undefined, checkpoints: checkpoints.slice(0, 3) });
            storage.setItem(key, JSON.stringify(slim));
            return slim;
        }
        catch (_c) {
            return null;
        }
    }
}
exports.writeEditWorkingStream = writeEditWorkingStream;
function clearEditWorkingStream(storage, personId, mode, rowId) {
    if (!storage)
        return;
    try {
        storage.removeItem(editWorkingStorageKey(personId, mode, rowId));
    }
    catch (_a) {
        /* ignore */
    }
}
exports.clearEditWorkingStream = clearEditWorkingStream;
/** True when working body differs from a baseline body string (e.g. server at open). */
function workingDiffersFromBody(working, baselineBody) {
    if (!working)
        return false;
    var a = working.body == null ? '' : String(working.body);
    var b = baselineBody == null ? '' : String(baselineBody);
    return a !== b;
}
exports.workingDiffersFromBody = workingDiffersFromBody;
